/**
 * MonsterDataLoader.js - Loads monsters from JSON files using new comprehensive schema
 * 
 * This service loads monster data from individual JSON files in the /data/monsters/ directory.
 * All monsters use the comprehensive monster schema defined in MonsterSchema.js.
 * 
 * FEATURES:
 * - Loads monsters from individual JSON files
 * - Validates schema compliance
 * - Provides fallback for missing monsters
 * - Supports both legacy and new schema formats
 * - Caches loaded monsters for performance
 */

import MonsterSchemaUtils from './MonsterSchema';
import { DEBUG_ENABLED } from '../utils/DebugLogger';

const debugLog = (...args) => {
  if (DEBUG_ENABLED) {
    console.debug(...args);
  }
};

class MonsterDataLoader {
  constructor() {
    this.monsterCache = new Map();
    this.loadingPromise = null;
    this.isLoaded = false;
  }

  /**
   * Dynamically load monster JSON files from the data/monsters directory
   */
  async loadAllMonsters() {
    debugLog('📚 loadAllMonsters() called');
    
    if (this.loadingPromise) {
      debugLog('⏳ Loading already in progress, returning existing promise...');
      return this.loadingPromise;
    }

    try {
      debugLog('🚀 Starting new monster loading process...');
      this.loadingPromise = this._loadMonstersInternal();
      await this.loadingPromise;
      
      // Only set isLoaded to true if we actually loaded some monsters
      if (this.monsterCache.size > 0) {
        debugLog(`✅ Successfully loaded ${this.monsterCache.size} monsters`);
        this.isLoaded = true;
      } else {
        debugLog('❌ No monsters were loaded into cache!');
        this.isLoaded = false;
      }
      
      return this.loadingPromise;
    } catch (error) {
        debugLog('❌ Error loading monsters:', error);
      this.isLoaded = false;
      this.loadingPromise = null;
      throw error;
    }
  }

  async _loadMonstersInternal() {
    debugLog('🔄 Starting monster loading process...');
    
    // Static imports for all monster files
    const monsterImports = {
      'goblin': () => import('../data/monsters/goblin.json'),
      'skeleton': () => import('../data/monsters/skeleton.json'),
      'zombie': () => import('../data/monsters/zombie.json'),
      'wolf': () => import('../data/monsters/wolf.json'),
      'dire-wolf': () => import('../data/monsters/dire-wolf.json'),
      'brown-bear': () => import('../data/monsters/brown-bear.json'),
      'bugbear': () => import('../data/monsters/bugbear.json'),
      'hobgoblin': () => import('../data/monsters/hobgoblin.json'),
      'orc': () => import('../data/monsters/orc.json'),
      'owlbear': () => import('../data/monsters/owlbear.json')
    };

    const monsterFileNames = Object.keys(monsterImports);
    debugLog(`📋 Attempting to load ${monsterFileNames.length} monsters:`, monsterFileNames);
    
    let loadedCount = 0;
    let errorCount = 0;
    
    for (const fileName of monsterFileNames) {
      try {
        debugLog(`📂 Loading ${fileName}.json...`);
        // Use static import functions
        const module = await monsterImports[fileName]();
        const data = module.default;
        
        if (!data || !data.data || !data.data.name) {
          debugLog(`❌ Invalid data format in ${fileName}.json`);
          errorCount++;
          continue;
        }
        
        debugLog(`📄 Raw data for ${fileName}:`, data.data.name);
        
        // Validate schema compliance
        try {
          MonsterSchemaUtils.validateMonster ? MonsterSchemaUtils.validateMonster(data) : this.basicValidate(data);
        } catch (validationError) {
          debugLog(`❌ Schema validation failed for ${fileName}:`, validationError.message);
          errorCount++;
          continue;
        }
        
        // Cache the monster
        this.monsterCache.set(fileName, data);
        const nameKey = data.data.name.toLowerCase().replace(/\s+/g, '_');
        this.monsterCache.set(nameKey, data);
        
        debugLog(`✅ Loaded monster: ${data.data.name} (${fileName})`);
        debugLog(`   📝 Cached with keys: '${fileName}' and '${nameKey}'`);
        loadedCount++;
      } catch (error) {
        debugLog(`❌ Failed to load monster ${fileName}:`, error);
        errorCount++;
      }
    }
    
    debugLog(`📊 Monster loading results: ${loadedCount} loaded, ${errorCount} errors`);
    debugLog(`🎯 Monster cache now contains ${this.monsterCache.size} entries`);
    debugLog('🔑 Cache keys:', Array.from(this.monsterCache.keys()));
    
    return loadedCount;
  }

  /**
   * Reload all monsters (useful for development)
   */
  async reloadMonsters() {
    debugLog('🔄 Reloading all monsters...');
    this.monsterCache.clear();
    this.loadingPromise = null;
    this.isLoaded = false;
    await this.loadAllMonsters();
  }

  /**
   * Clear cache for a specific monster
   */
  clearMonsterCache(monsterId) {
    debugLog(`🚮 Clearing cache for monster: ${monsterId}`);
    this.monsterCache.delete(monsterId);
  }

  /**
   * Clear all cache
   */
  clearAllCache() {
    debugLog('🗑️ Clearing all monster cache and resetting loader state');
    this.monsterCache.clear();
    this.loadingPromise = null;
    this.isLoaded = false;
  }

  /**
   * Basic validation for monster data
   */
  basicValidate(monster) {
    const required = ['schemaVersion', 'type', 'id', 'data'];
    const dataRequired = ['name', 'size', 'creatureType', 'alignment', 'cr', 'ac', 'hp', 'speed', 'abilities'];
    
    for (const field of required) {
      if (!monster[field]) {
        throw new Error(`Missing required field: ${field}`);
      }
    }
    
    for (const field of dataRequired) {
      if (!monster.data[field]) {
        throw new Error(`Missing required data field: ${field}`);
      }
    }
    
    // Validate that abilities object has required stats
    if (monster.data.abilities) {
      const requiredStats = ['str', 'dex', 'con', 'int', 'wis', 'cha'];
      for (const stat of requiredStats) {
        if (typeof monster.data.abilities[stat] !== 'number') {
          throw new Error(`Missing or invalid ability score: ${stat}`);
        }
      }
    }
    
    return true;
  }

  /**
   * Get monster by ID or name with enhanced custom monster handling
   */
  async getMonster(identifier) {
    debugLog(`🔍 MonsterDataLoader.getMonster('${identifier}') called`);
    
    // Handle null or undefined identifier
    if (!identifier) {
      debugLog('❌ Invalid monster identifier: null or undefined');
      throw new Error('Invalid monster identifier');
    }
    
    // Ensure monsters are loaded
    if (!this.isLoaded) {
      debugLog('⏳ Loading monsters...');
      await this.loadAllMonsters();
    }

    if (this.monsterCache.size === 0) {
      throw new Error('Failed to load any monsters into cache');
    }

    debugLog(`🔑 Cache keys: [${Array.from(this.monsterCache.keys()).join(', ')}]`);
    
    // Check if this is a custom monster ID
    if (identifier.startsWith('custom_monster_')) {
      debugLog('🔍 Detected request for custom monster with ID:', identifier);
      try {
        // Try to load from CustomAssetManager directly
        const { default: CustomAssetManager } = await import('./CustomAssetManager');
        const customMonster = CustomAssetManager.loadCustomAsset(identifier);
        
        if (customMonster) {
          debugLog('✅ Found custom monster with direct ID lookup:', customMonster.name);
          
          // Check if customData has a nested data structure
          if (customMonster.customData && customMonster.customData.data) {
            debugLog('🔄 Returning custom monster with schema structure');
            return {
              id: identifier,
              schemaVersion: "1.0",
              type: "monster",
              data: customMonster.customData.data
            };
          } else {
            // Otherwise wrap the customData itself
            debugLog('🔄 Returning custom monster with flat structure');
            return {
              id: identifier,
              schemaVersion: "1.0",
              type: "monster",
              data: customMonster.customData
            };
          }
        }
      } catch (error) {
        debugLog('⚠️ Failed to load custom monster directly:', error);
        // Continue with standard lookup methods
      }
    }
    
    // Comprehensive lookup strategy
    const lookupStrategies = [
      // 1. Direct lookup
      identifier,
      // 2. Lowercase
      identifier.toLowerCase(),
      // 3. Normalize spaces to underscores
      identifier.toLowerCase().replace(/\s+/g, '_'),
      // 4. Normalize spaces to hyphens
      identifier.toLowerCase().replace(/\s+/g, '-'),
      // 5. Remove all spaces
      identifier.toLowerCase().replace(/\s+/g, ''),
    ];
    
    for (const strategy of lookupStrategies) {
      if (this.monsterCache.has(strategy)) {
        debugLog(`✅ Found monster using strategy '${strategy}'`);
        return this.monsterCache.get(strategy);
      }
    }
    
    // Try fuzzy matching
    for (const [key, value] of this.monsterCache.entries()) {
      const keyLower = key.toLowerCase();
      const identifierLower = identifier.toLowerCase();
      
      // Check if names match when normalized
      if (keyLower.replace(/[-_\s]/g, '') === identifierLower.replace(/[-_\s]/g, '')) {
        debugLog(`✅ Found monster by fuzzy match: '${key}' ≈ '${identifier}'`);
        return value;
      }
    }
    
    // Last resort - try checking CustomAssetManager by name if it's not already an ID
    if (!identifier.startsWith('custom_monster_')) {
      try {
        const { default: CustomAssetManager } = await import('./CustomAssetManager');
        const customAssets = await CustomAssetManager.getAllCustomAssets();
        
        const normalizeId = (id) => id ? id.toString().toLowerCase().replace(/[\s_-]/g, '') : '';
        const searchId = normalizeId(identifier);
        
        debugLog(`🔍 Last resort: searching for custom monster by normalized name: ${searchId}`);
        
        const customMonster = customAssets.monsters.find(m => {
          if (!m) return false;
          
          // Check various name fields with normalization
          return normalizeId(m.name) === searchId || 
                 (m.customData && normalizeId(m.customData.name) === searchId) ||
                 (m.customData && m.customData.data && normalizeId(m.customData.data.name) === searchId);
        });
        
        if (customMonster) {
          debugLog(`✅ Found custom monster by name: ${customMonster.name}`);
          
          // Check if customData has a nested data structure
          if (customMonster.customData && customMonster.customData.data) {
            return {
              id: customMonster.id,
              schemaVersion: "1.0",
              type: "monster",
              data: customMonster.customData.data
            };
          } else {
            // Otherwise wrap the customData itself
            return {
              id: customMonster.id,
              schemaVersion: "1.0",
              type: "monster",
              data: customMonster.customData
            };
          }
        }
      } catch (error) {
        debugLog('❌ Error in custom monster name lookup:', error);
      }
    }
    
    // If all else fails
    debugLog(`❌ Monster '${identifier}' not found. Available: ${Array.from(this.monsterCache.keys()).join(', ')}`);
    throw new Error(`Monster '${identifier}' not found`);
  }

  /**
   * Get all available monsters
{{ ... }}
   */
  async getAllMonsters() {
    // Ensure monsters are loaded
    if (!this.isLoaded) {
      await this.loadAllMonsters();
    }
    
    const monsters = {};
    for (const [id, data] of this.monsterCache.entries()) {
      // Only include primary IDs (not aliases)
      if (id === data.id) {
        monsters[id] = data;
      }
    }
    return monsters;
  }

  /**
   * Get monsters by CR range
   */
  async getMonstersByCR(minCR = 0, maxCR = 30) {
    // Ensure monsters are loaded
    if (!this.isLoaded) {
      await this.loadAllMonsters();
    }
    
    const monsters = {};
    for (const [id, data] of this.monsterCache.entries()) {
      if (id === data.id && data.data.cr.value >= minCR && data.data.cr.value <= maxCR) {
        monsters[id] = data;
      }
    }
    return monsters;
  }

  /**
   * Get monsters by type
   */
  getMonstersByType(creatureType) {
    const monsters = {};
    for (const [id, data] of this.monsterCache.entries()) {
      if (id === data.id && data.data.creatureType.type.toLowerCase() === creatureType.toLowerCase()) {
        monsters[id] = data;
      }
    }
    return monsters;
  }

  /**
   * Get monsters by environment
   */
  getMonstersByEnvironment(environment) {
    const monsters = {};
    for (const [id, data] of this.monsterCache.entries()) {
      if (id === data.id && data.data.environments && 
          data.data.environments.some(env => env.toLowerCase() === environment.toLowerCase())) {
        monsters[id] = data;
      }
    }
    return monsters;
  }

  /**
   * Search monsters by name or tags
   */
  searchMonsters(query) {
    const results = {};
    const searchTerm = query.toLowerCase();
    
    for (const [id, data] of this.monsterCache.entries()) {
      if (id === data.id) {
        const name = data.data.name.toLowerCase();
        const tags = data.tags ? data.tags.join(' ').toLowerCase() : '';
        
        if (name.includes(searchTerm) || tags.includes(searchTerm)) {
          results[id] = data;
        }
      }
    }
    return results;
  }

  /**
   * Convert new schema monster to legacy format for backward compatibility
   */
  /**
   * Converts monster data from comprehensive schema to legacy format
   * Enhanced with comprehensive fallback chains for data preservation and transformation
   * 
   * @date_of_change 2025-08-23 - Enhanced with comprehensive fallback chains and debug logging
   * @param {Object} monster - Monster object with data property
   * @returns {Object} - Legacy format monster data
   */
  toLegacyFormat(monster) {
    if (!monster) {
      console.warn('[WARNING] toLegacyFormat called with null/undefined monster');  
      return {};
    }

    // Enhanced debug logging for data transformation
    console.debug('[DEBUG:DATA_TRANSFORMATION] toLegacyFormat input:', {
      id: monster.id,
      name: monster.name || monster.data?.name,
      hasData: Boolean(monster.data),
      hasHP: monster.hp !== undefined,
      hasCR: Boolean(monster.cr),
      inputKeys: Object.keys(monster)
    });
    
    // Handle both direct properties and nested data structure
    const data = monster.data || monster;
    
    // Extract lore data from traits
    const traits = data.traits || monster.traits || data.specialAbilities || monster.specialAbilities || [];
    const loreData = this.extractLoreFromTraits(traits);
    
    // Filter out lore traits from regular traits
    const regularTraits = traits.filter(trait => 
      trait && trait.name && !['Lore', 'Habitat', 'Tactics'].includes(trait.name)
    );
    
    // Robust fallback getter function
    const get = (obj, paths, defaultValue) => {
      for (const path of paths) {
        const value = path.split('.').reduce((o, key) => o && o[key] !== undefined ? o[key] : undefined, obj);
        if (value !== undefined) return value;
      }
      return defaultValue;
    };
    
    // Get CR with fallbacks
    const cr = get(monster, [
      'cr', 
      'data.cr.display', 
      'data.cr.value',
      'data.challengeRating',
      'challenge_rating',
      'challengeRating'
    ], '1/4');
    
    // Calculate XP from CR if needed
    const xp = get(monster, [
      'xp', 
      'data.xp',
      'experiencePoints'
    ], this.calculateXPFromCR(cr));
    
    // HP handling with preservation logic
    const currentHP = get(monster, [
      'hp',
      'data.hp.current',
      'hpObj.current',
      'data.hp.average',
      'data.hp.value',
      'hitPoints'
    ]);
    
    const maxHP = get(monster, [
      'maxHp',
      'data.hp.max',
      'hpObj.max',
      'data.hp.average',
      'data.maxHp',
      'data.hp.value'
    ]);
    
    // Get type with fallbacks
    const monsterType = get(monster, [
      'type', 
      'monsterType',
      'data.creatureType.type',
      'data.type',
      'creatureType.type',
      'creature_type.type'
    ], 'humanoid');
    
    // Get ability scores with fallbacks
    const abilities = get(monster, [
      'abilities',
      'stats',
      'data.abilities',
      'data.stats'
    ], { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 });
    
    // Debug the field transformations
    console.debug('[DEBUG:DATA_TRANSFORMATION] Key field transformations:', {
      originalCR: monster.cr,
      extractedCR: cr,
      originalHP: monster.hp,
      extractedHP: currentHP,
      extractedMaxHP: maxHP,
      originalType: monster.type,
      extractedType: monsterType
    });
    
    // Build a comprehensive legacy format with proper fallbacks
    const legacyFormat = {
      // Core identification
      id: monster.id || monster.templateId || 'unknown',
      name: get(monster, ['name', 'data.name'], 'Unknown Monster'),
      size: get(monster, ['size', 'data.size'], 'Medium'),
      type: monsterType !== 'monster' ? monsterType : 'humanoid',
      alignment: get(monster, ['alignment', 'data.alignment'], 'neutral'),
      
      // Combat stats
      cr: typeof cr === 'object' ? cr.display || cr.value : cr,
      xp: xp,
      ac: get(monster, ['ac', 'data.ac.value', 'armorClass'], 10),
      
      // HP with preservation logic
      hp: currentHP !== undefined ? currentHP : (maxHP || 10),
      maxHp: maxHP || currentHP || 10,
      
      // Movement
      speed: get(monster, ['speed.walk', 'data.speed.walk', 'speed'], 30),
      climbSpeed: get(monster, ['speed.climb', 'data.speed.climb'], null),
      flySpeed: get(monster, ['speed.fly', 'data.speed.fly'], null),
      swimSpeed: get(monster, ['speed.swim', 'data.speed.swim'], null),
      burrowSpeed: get(monster, ['speed.burrow', 'data.speed.burrow'], null),
      
      // Core attributes
      initiative: get(monster, ['initiative'], Math.floor((abilities.dex - 10) / 2)),
      stats: { ...abilities }, // Create a copy to avoid reference issues
      abilities: { ...abilities }, // Include both formats for compatibility
      
      // Combat abilities
      savingThrows: get(monster, ['savingThrows', 'data.saves'], {}),
      skills: this.convertSkillsToLegacy(get(monster, ['skills', 'data.skills'], {})),
      damageResistances: this.convertDamageListToLegacy(get(monster, ['damageResistances', 'data.damageModifiers.resistances'], [])),
      damageImmunities: this.convertDamageListToLegacy(get(monster, ['damageImmunities', 'data.damageModifiers.immunities'], [])),
      damageVulnerabilities: this.convertDamageListToLegacy(get(monster, ['damageVulnerabilities', 'data.damageModifiers.vulnerabilities'], [])),
      conditionImmunities: get(monster, ['conditionImmunities', 'data.conditionImmunities'], []),
      
      // Senses and perception
      senses: get(monster, ['senses', 'data.senses.notes'], 
                  `Passive Perception ${get(monster, ['data.senses.passivePerception'], 10)}`),
      darkvision: get(monster, ['darkvision', 'data.senses.darkvision'], null),
      blindsight: get(monster, ['blindsight', 'data.senses.blindsight'], null),
      
      // Language
      languages: get(monster, ['languages', 'data.languages.list'], []),
      languageNotes: get(monster, ['languageNotes', 'data.languages.understandsBut'], null),
      
      // Actions and abilities
      actions: this.convertActionsToLegacy(get(monster, ['actions', 'data.actions'], [])),
      specialAbilities: this.convertActionsToLegacy(regularTraits),
      
      // Lore and imagery
      lore: monster.lore || loreData || {},
      artwork: get(monster, ['artwork', 'data.images.artwork', 'image'], `/assets/monsters/${monster.id || 'unknown'}.png`),
      image: get(monster, ['image', 'data.images.artwork'], `/assets/monsters/${monster.id || 'unknown'}.png`),
      token: get(monster, ['token', 'data.images.token'], `/assets/monsters/tokens/${monster.id || 'unknown'}.png`),
      
      // Preserve schema version for tracking
      schemaVersion: monster.schemaVersion || '1.0'
    };
    
    // Preserve any custom fields not explicitly handled
    const preservedCustomFields = {};
    const standardFields = [
      'id', 'name', 'size', 'type', 'alignment', 'cr', 'ac', 'hp', 'maxHp', 
      'speed', 'initiative', 'stats', 'abilities', 'savingThrows', 'skills',
      'damageResistances', 'damageImmunities', 'damageVulnerabilities',
      'conditionImmunities', 'senses', 'languages', 'actions', 'specialAbilities',
      'lore', 'artwork', 'image', 'token', 'data', 'traits', 'xp'
    ];
    
    Object.keys(monster).forEach(key => {
      if (!standardFields.includes(key) && typeof monster[key] !== 'function') {
        preservedCustomFields[key] = monster[key];
        console.debug(`[DEBUG:MonsterDataLoader] Preserving custom field: ${key}`);
      }
    });
    
    // Apply preserved custom fields
    Object.assign(legacyFormat, preservedCustomFields);
    
    // Final debug log
    console.debug('[DEBUG:DATA_TRANSFORMATION] toLegacyFormat output:', {
      id: legacyFormat.id,
      name: legacyFormat.name,
      cr: legacyFormat.cr,
      hp: legacyFormat.hp,
      maxHp: legacyFormat.maxHp,
      outputKeys: Object.keys(legacyFormat)
    });
    
    return legacyFormat;
  }

  /**
   * Extract lore data from traits array
   */
  extractLoreFromTraits(traits) {
    if (!traits) return {};
    
    const lore = {};
    traits.forEach(trait => {
      switch (trait.name) {
        case 'Lore':
          lore.description = trait.text || trait.description;
          break;
        case 'Habitat':
          lore.habitat = trait.text || trait.description;
          break;
        case 'Tactics':
          lore.tactics = trait.text || trait.description;
          break;
        default:
          break;
      }
    });
    
    // Debug logging for lore extraction
    if (Object.keys(lore).length > 0) {
      console.log('🔍 Extracted lore data:', lore);
    }
    
    return lore;
  }

  convertSkillsToLegacy(skills) {
    if (!skills) return {};
    const legacy = {};
    for (const [skill, data] of Object.entries(skills)) {
      // Handle both old format {bonus: X, expertise: Y} and new format {bonus: X}
      legacy[skill] = typeof data === 'object' ? data.bonus : data;
    }
    return legacy;
  }

  convertDamageListToLegacy(damageList) {
    if (!damageList) return [];
    return damageList.map(dmg => dmg.type);
  }

  convertActionsToLegacy(actions) {
    if (!actions) return [];
    return actions.map(action => ({
      name: action.name,
      description: action.text || this.generateActionDescription(action)
    }));
  }

  generateActionDescription(action) {
    if (action.attack) {
      const attack = action.attack;
      let desc = `${attack.type === 'meleeWeapon' ? 'Melee' : 'Ranged'} Weapon Attack: `;
      desc += `+${attack.toHit} to hit, `;
      if (attack.reach) desc += `reach ${attack.reach} ft., `;
      if (attack.range) desc += `range ${attack.range.normal}/${attack.range.long} ft., `;
      desc += `${attack.targets || 'one target'}. `;
      if (attack.damage && attack.damage.length > 0) {
        const dmg = attack.damage[0];
        const avgDamage = dmg.avg || this.calculateAverageDamage(dmg.dice);
        desc += `Hit: ${avgDamage} (${dmg.dice}) ${dmg.type} damage.`;
      }
      if (attack.riders) desc += ` ${attack.riders}`;
      if (attack.save) {
        const save = attack.save;
        desc += ` Target must make a DC ${save.dc} ${save.ability} saving throw.`;
        if (save.notes) desc += ` ${save.notes}.`;
      }
      if (attack.notes) desc += ` ${attack.notes}.`;
      return desc;
    }
    return action.text || 'No description available.';
  }

  calculateAverageDamage(diceFormula) {
    // Simple calculation for common dice formulas like "1d6+2", "2d4+2"
    const match = diceFormula.match(/(\d+)d(\d+)(?:\+(\d+))?/);
    if (match) {
      const numDice = parseInt(match[1]);
      const dieSize = parseInt(match[2]);
      const modifier = parseInt(match[3]) || 0;
      return Math.floor(numDice * (dieSize + 1) / 2) + modifier;
    }
    return 0;
  }
  
  /**
   * Calculate XP value based on Challenge Rating
   * 
   * @date_of_change 2025-08-23
   * @param {string|number} cr - Challenge rating as string or number
   * @returns {number} - XP value
   */
  calculateXPFromCR(cr) {
    // Handle string CR values with fractions like "1/4", "1/2" etc.
    let numericCR = cr;
    if (typeof cr === 'string') {
      // Handle fraction format
      if (cr.includes('/')) {
        const parts = cr.split('/');
        numericCR = parseInt(parts[0]) / parseInt(parts[1]);
      } else {
        // Handle numeric strings
        numericCR = parseFloat(cr);
      }
    }
    
    // Standard 5e XP values by CR
    const crXPTable = {
      0: 10,
      '1/8': 25,
      0.125: 25,
      '1/4': 50,
      0.25: 50,
      '1/2': 100,
      0.5: 100,
      1: 200,
      2: 450,
      3: 700,
      4: 1100,
      5: 1800,
      6: 2300,
      7: 2900,
      8: 3900,
      9: 5000,
      10: 5900,
      11: 7200,
      12: 8400,
      13: 10000,
      14: 11500,
      15: 13000,
      16: 15000,
      17: 18000,
      18: 20000,
      19: 22000,
      20: 25000,
      21: 33000,
      22: 41000,
      23: 50000,
      24: 62000,
      25: 75000,
      26: 90000,
      27: 105000,
      28: 120000,
      29: 135000,
      30: 155000
    };
    
    // Look up exact CR match
    if (crXPTable[numericCR] !== undefined) {
      return crXPTable[numericCR];
    }
    
    // For non-standard CR values, find nearest match
    const crValues = Object.keys(crXPTable)
      .filter(key => !isNaN(parseFloat(key)))
      .map(key => parseFloat(key))
      .sort((a, b) => a - b);
    
    // Find nearest CR value
    let nearest = crValues[0];
    for (const val of crValues) {
      if (Math.abs(val - numericCR) < Math.abs(nearest - numericCR)) {
        nearest = val;
      }
    }
    
    console.debug(`[DEBUG:XP] Calculated XP for CR ${cr} using nearest CR ${nearest}: ${crXPTable[nearest]}`);
    return crXPTable[nearest] || 0;
  }
}

// Create singleton instance
const monsterDataLoader = new MonsterDataLoader();

export default monsterDataLoader;
